import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PageHeader from "../../../../core/common/crmComponents/PageHeader";
import PaginationControls from "../../../../core/common/pagination";
import { useSearchParams } from "react-router-dom";
import {
  MODULES,
  MODULES_ACTIONS,
} from "../../../../core/data/constants/moduleConstants";
import HasPermission from "../../../../core/common/wrapper/HasPermission";
import {
  getUrlParams,
  setUrlParams,
} from "../../../../utils/helpers/setAndGetUrlParams";
import { generateQueryParams } from "../../../../utils/helpers/generateQueryParams";
import { useDuplicateLeadsData } from "../hooks/useDuplicateLeadsData";

import LoaderComponent from "../../../../core/common/crmComponents/LoaderComponent";
import DuplicateLeadList from "../components/DuplicateLeadList";
import DuplicateLeadGridLayout from "../components/DuplicateLeadGridLayout";
import CardHeader from "../components/CardHeader";
import { setReloading } from "../../../../core/data/redux/moduleDataSlice";
import queryClient from "../../../../config/queryClient";

const DuplicateLeads = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const user = JSON.parse(localStorage.getItem("user") || []);
  const [layout, setLayout] = useState("list");
  const { companyNameArr } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const reloading = useSelector((state) => state.moduleData.reloading);

  const initialLimit = getUrlParams("limit")
    ? Math.ceil(getUrlParams("limit"))
    : 50;
  const initialPage = getUrlParams("page") ? Number(getUrlParams("page")) : 1;
  const initialCompany = getUrlParams("companyid")
    ? getUrlParams("companyid")
    : user?.ofCompany?._id ||
      (companyNameArr.length > 0 ? companyNameArr[0].value : null);

  const [limit, setLimit] = useState(initialLimit);
  const [page, setPage] = useState(initialPage);
  const [selectedCompany, setSelectedCompany] = useState(initialCompany);

  const toggleLayout = () => {
    setLayout((prevLayout) => (prevLayout === "list" ? "grid" : "list"));
  };

  const { filters } = useSelector((state) => state.filter);
  const duplicateLeadsFilters = filters.duplicateLeads || [];

  const { queryParams, params } = generateQueryParams(duplicateLeadsFilters);

  const { data, isLoading, isError, error, isFetching } = useDuplicateLeadsData(
    {
      page: page ? page : 1,
      limit: limit ? limit : 50,
      ofCompany: selectedCompany ? selectedCompany : null,
      queryString: params.toString(),
      queryParams: queryParams.toString(),
    }
  );

  const leadsData = data?.data?.data;
  const [filteredData, setFilteredData] = useState(leadsData);

  const totalCount = data?.data?.totalResult;
  const totalPages = Math.ceil(totalCount / limit);

  useEffect(() => {
    if (!searchParams.get("companyid")) {
      setUrlParams(setSearchParams, "companyid", initialCompany);
    }
    setSelectedCompany(initialCompany);
  }, [initialCompany]);

  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages); // Reset to the last available page
      setUrlParams(setSearchParams, "page", totalPages);
    }
  }, [totalPages, page, setSearchParams]);

  useEffect(() => {
    if (searchParams.get("page") !== String(page)) {
      setUrlParams(setSearchParams, "page", page);
    }
  }, [page]);

  useEffect(() => {
    if (selectedCompany) {
      setUrlParams(setSearchParams, "companyid", selectedCompany);
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (
      searchParams.get("page") &&
      searchParams.get("limit") !== String(limit)
    ) {
      setUrlParams(setSearchParams, "limit", limit);
    }
  }, [limit]);

  const handlePageChange = (newPage) => {
    setPage(newPage); // Update page state to trigger refetch
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1); // Reset page to 1 whenever limit changes
  };

  useEffect(() => {
    if (duplicateLeadsFilters?.length > 0) {
      setPage(1);
    }
  }, [duplicateLeadsFilters]);

  useEffect(() => {
    if (reloading) {
      queryClient.invalidateQueries(["duplicateLeads"]);
      dispatch(setReloading(false));
    }
  }, [reloading, dispatch]);

  return (
    <>
      <div className="page-wrapper">
        <div className="content custom-content-padding">
          <div className="row">
            <div className="col-md-12">
              <PageHeader
                pageName="Duplicate Leads"
                totalCount={totalCount || 0}
              />
              <div className="card mb-0">
                <CardHeader
                  layout={layout}
                  toggleLayout={toggleLayout}
                  selectedCompany={selectedCompany}
                  setSelectedCompany={setSelectedCompany}
                />
                <div className="card-body py-2">
                  <PaginationControls
                    totalPages={totalPages}
                    totalCount={totalCount}
                    currentPageDataCount={
                      filteredData?.length > 0
                        ? filteredData?.length
                        : Math.min(leadsData?.length, limit)
                    }
                    onPageChange={handlePageChange}
                    onLimitChange={handleLimitChange}
                    moduleName={MODULES.LEADS}
                  />

                  {isLoading ? (
                    <LoaderComponent />
                  ) : isError ? (
                    <div>Error: {error.message}</div>
                  ) : layout === "list" ? (
                    <DuplicateLeadList
                      leadsData={leadsData}
                      selectedCompany={selectedCompany}
                      setFilteredData={setFilteredData}
                    />
                  ) : (
                    <DuplicateLeadGridLayout leadsData={leadsData} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DuplicateLeads;
